import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  fetchEventGallery,
  buildThumbUrl,
  buildOriginalUrl,
  downloadPhoto,
} from "../../../api/publicPhotos";
import "./eventGallery.css";

// Public per-event photo gallery (no login needed). Linked from the
// homepage's Recent Event Photos section. Shows every approved photo
// for one event with uploader attribution and easy downloads on any
// device — tap a photo for the fullscreen viewer, Download saves the
// full-size original (long-press works too since images are plain
// public URLs).

const fmtDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const EventGallery = () => {
  const { socialEventId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    fetchEventGallery(socialEventId)
      .then((data) => {
        if (alive) setGroup(data.event || null);
      })
      .catch((err) => {
        if (alive) setError(err.message || "Couldn't load photos.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [socialEventId]);

  const photos = (group && group.photos) || [];

  const closeViewer = useCallback(() => setViewerIndex(null), []);
  const step = useCallback(
    (delta) => {
      setViewerIndex((idx) => {
        if (idx == null || photos.length === 0) return idx;
        return (idx + delta + photos.length) % photos.length;
      });
    },
    [photos.length]
  );

  useEffect(() => {
    if (viewerIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerIndex, closeViewer, step]);

  const handleDownload = async (photo) => {
    setDownloading(photo.s3Key);
    try {
      await downloadPhoto(photo.s3Key, photo.displayName || undefined);
    } finally {
      setDownloading("");
    }
  };

  const title =
    (group && group.title) ||
    String(socialEventId || "")
      .replace(/^\d{4}-\d{2}-\d{2}-/, "")
      .replace(/-/g, " ");

  const viewerPhoto = viewerIndex != null ? photos[viewerIndex] : null;

  return (
    <div className="event-gallery">
      <div className="event-gallery__topbar">
        <Link to="/" className="event-gallery__back">
          ← activebeaches.com
        </Link>
        <button
          type="button"
          className="event-gallery__join"
          onClick={() => navigate("/join")}
        >
          JOIN
        </button>
      </div>

      <h1 className="event-gallery__title">{title}</h1>
      <div className="event-gallery__meta">
        {fmtDate(group && group.date)}
        {group ? ` · ${group.photoCount} photo${group.photoCount === 1 ? "" : "s"}` : ""}
      </div>
      <p className="event-gallery__hint">
        Tap a photo to view fullscreen — Download saves the full-size
        original to your phone or computer.
      </p>

      {loading && <p style={{ color: "#6a7385" }}>Loading photos…</p>}
      {error && <div className="event-gallery__error">{error}</div>}
      {!loading && !error && photos.length === 0 && (
        <p style={{ color: "#6a7385" }}>No photos here yet.</p>
      )}

      <div className="event-gallery__grid">
        {photos.map((p, i) => (
          <div className="event-gallery__card" key={p.s3Key}>
            <button
              type="button"
              className="event-gallery__imgbtn"
              onClick={() => setViewerIndex(i)}
            >
              <img
                src={buildThumbUrl(p.s3Key)}
                alt={p.displayName || "Event photo"}
                loading="lazy"
                onError={(e) => {
                  // No thumbnail generated yet — fall back to the original.
                  if (e.target.src !== buildOriginalUrl(p.s3Key)) {
                    e.target.src = buildOriginalUrl(p.s3Key);
                  }
                }}
              />
            </button>
            <div className="event-gallery__cardfoot">
              <span className="event-gallery__uploader">
                {p.uploaderName ? `📸 ${p.uploaderName}` : ""}
              </span>
              <button
                type="button"
                className="event-gallery__dl"
                onClick={() => handleDownload(p)}
                disabled={downloading === p.s3Key}
              >
                {downloading === p.s3Key ? "Saving…" : "⬇ Download"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {viewerPhoto && (
        <div className="event-gallery__viewer" role="dialog" aria-modal="true">
          <div className="event-gallery__viewer-top">
            <span className="event-gallery__viewer-name">
              {viewerPhoto.displayName || ""}
              {viewerPhoto.uploaderName
                ? `  ·  📸 ${viewerPhoto.uploaderName}`
                : ""}
            </span>
            <button
              type="button"
              className="event-gallery__viewer-close"
              onClick={closeViewer}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="event-gallery__viewer-img">
            <img
              src={buildOriginalUrl(viewerPhoto.s3Key)}
              alt={viewerPhoto.displayName || "Event photo"}
            />
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  className="event-gallery__viewer-nav event-gallery__viewer-nav--prev"
                  onClick={() => step(-1)}
                  aria-label="Previous photo"
                />
                <button
                  type="button"
                  className="event-gallery__viewer-nav event-gallery__viewer-nav--next"
                  onClick={() => step(1)}
                  aria-label="Next photo"
                />
              </>
            )}
          </div>
          <div className="event-gallery__viewer-bottom">
            <span>
              {viewerIndex + 1} / {photos.length}
            </span>
            <button
              type="button"
              className="event-gallery__viewer-dl"
              onClick={() => handleDownload(viewerPhoto)}
              disabled={downloading === viewerPhoto.s3Key}
            >
              {downloading === viewerPhoto.s3Key
                ? "Saving…"
                : "⬇ Download"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventGallery;
